import React, { useRef, useState } from 'react';
import {
  FileSpreadsheet,
  Upload,
  AlertCircle,
  X,
  Check,
} from 'lucide-react';

interface FinalExcelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Uploads the file that was actually sent to ΔΕΔΔΗΕ and locks the days it
   * covers. The upload on its own only stores a draft, so the caller finalizes
   * straight after. Rejects with a message to show in the dialog.
   */
  onImportFile: (file: File) => Promise<void>;
  /**
   * Locks the selected days with the values that were exported, for when the
   * file was sent unchanged and there is nothing to upload.
   */
  onConfirmWithoutFile: () => Promise<boolean> | void;
}

export const FinalExcelDialog: React.FC<FinalExcelDialogProps> = ({
  isOpen,
  onClose,
  onImportFile,
  onConfirmWithoutFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleConfirmUiFinal = async () => {
    setIsFinalizing(true);
    setUploadError(null);

    try {
      // Finalizing can be rejected as a whole — a locked day with no new file,
      // or a revision the server could not apply — and the dialog is where the
      // operator is standing, so it stays open to say so.
      const finalized = await onConfirmWithoutFile();

      if (finalized === false) {
        setUploadError(
          'Οι ημέρες δεν οριστικοποιήθηκαν. Δείτε το μήνυμα σφάλματος και δοκιμάστε ξανά.'
        );

        return;
      }

      onClose();
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      await onImportFile(file);
      onClose();
    } catch (err: any) {
      setUploadError(
        err?.message ||
          'Αποτυχία ανάγνωσης του αρχείου Excel. Παρακαλώ βεβαιωθείτε ότι είναι έγκυρο αρχείο ΔΕΔΔΗΕ.'
      );
    } finally {
      setIsUploading(false);
      // Let the same file be picked again after a failure.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg rounded-3xl border border-gray-100 bg-white p-6 shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-xl bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Icon */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/20">
          <FileSpreadsheet className="h-7 w-7" />
        </div>

        {/* Title */}
        <h3 className="text-center text-xl font-bold text-gray-900">
          Οριστικοποίηση Προσφοράς ΔΕΔΔΗΕ
        </h3>
        <p className="mt-2 text-center text-sm text-gray-600">
          Κατεβάσατε το αρχείο προσφοράς. Πραγματοποιήσατε τοπικές αλλαγές στο Excel πριν την αποστολή στον ΔΕΔΔΗΕ;
        </p>
        <p className="mt-1 text-center text-xs text-gray-500">
          Και οι δύο επιλογές οριστικοποιούν τις ημέρες — η μεταφόρτωση του αρχείου
          καταγράφει επιπλέον τι στάλθηκε πραγματικά.
        </p>

        {uploadError && (
          <div className="mt-4 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <span>{uploadError}</span>
          </div>
        )}

        {/* Action Options */}
        <div className="mt-6 space-y-3">
          {/* Option A: acknowledge without uploading */}
          <button
            onClick={handleConfirmUiFinal}
            disabled={isUploading || isFinalizing}
            className="group flex w-full items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 text-left transition hover:border-emerald-300 hover:bg-emerald-100/70 disabled:opacity-50"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                <Check className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-emerald-950">
                  {isFinalizing ? 'Οριστικοποίηση...' : 'Όχι, η προσφορά είναι οριστική'}
                </p>
                <p className="text-xs text-emerald-800">
                  Στάλθηκε όπως εξήχθη — κλείδωμα των ημερών με τις τιμές αυτές
                </p>
              </div>
            </div>
          </button>

          {/* Option B: upload the file that was sent */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isFinalizing}
            className="group flex w-full items-center justify-between rounded-2xl border border-blue-200 bg-blue-50/60 p-4 text-left transition hover:border-blue-300 hover:bg-blue-100/70 disabled:opacity-50"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
                <Upload className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-blue-950">
                  {isUploading ? 'Μεταφόρτωση...' : 'Ναι, μεταφόρτωση οριστικού Excel'}
                </p>
                <p className="text-xs text-blue-800">
                  Εισαγωγή του τροποποιημένου αρχείου `.xlsx` που εστάλη και κλείδωμα
                </p>
              </div>
            </div>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default FinalExcelDialog;
